import React, { useState, useEffect, useCallback } from "react";
import { Star, Plus, Minus, Trash2, Check, UserPlus, Loader2, History, Clock, Users, Eye, EyeOff, Trophy, MessageCircle, KeyRound, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, formatDateTime } from "@/lib/api";
import { STANDARDS } from "@/lib/standards";
import { AdmissionQrCard, AdmissionDetails } from "@/pages/admin/Admission";

const fmt = formatDateTime;

function TeacherResetPasswordDialog({ teacher, onDone }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!pw || pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.patch(`/admin/teachers/${teacher.id}`, { password: pw });
      toast.success("Password reset — share the new password with the teacher");
      setPw(""); setOpen(false); onDone && onDone();
    } catch { toast.error("Failed to reset password"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" title="Reset password" data-testid={`reset-teacher-pw-${teacher.id}`}><KeyRound className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset password — {teacher.name}</DialogTitle>
          <DialogDescription>Set a new login password for {teacher.email}. Share it with them; they can sign in with it right away.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="mb-1.5 block font-semibold text-slate-700">New password</Label>
            <Input value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-teacher-pw-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-950" data-testid="reset-teacher-pw-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeacherWhatsAppDialog({ teacher, onDone }) {
  const [open, setOpen] = useState(false);
  const [wa, setWa] = useState(teacher.whatsapp || "");
  const [consent, setConsent] = useState(!!teacher.whatsapp_consent);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setWa(teacher.whatsapp || ""); setConsent(!!teacher.whatsapp_consent); } }, [open]); // eslint-disable-line
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/admin/teachers/${teacher.id}`, { whatsapp: wa, whatsapp_consent: consent }); toast.success("WhatsApp details saved"); setOpen(false); onDone && onDone(); }
    catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`teacher-whatsapp-${teacher.id}`}><MessageCircle className="h-4 w-4" /></Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>WhatsApp — {teacher.name}</DialogTitle>
          <DialogDescription>Indian mobile number for attendance reminders. Reminders only send with consent (test mode until Meta is configured).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="mb-1.5 block font-semibold text-slate-700">WhatsApp number</Label><Input value={wa} onChange={(e) => setWa(e.target.value)} placeholder="9833388201" data-testid={`wa-number-${teacher.id}`} /></div>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="h-4 w-4 accent-blue-900" data-testid={`wa-consent-${teacher.id}`} />
            Consent to receive WhatsApp reminders
          </label>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-950" data-testid={`wa-save-${teacher.id}`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeacherAssignDialog({ triggerNode, title, actionLabel, teachers, initial, onSave }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(initial || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setSel(initial || []); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const save = async () => {
    if (sel.length === 0) { toast.error("Please select at least one teacher"); return; }
    setSaving(true);
    try { await onSave(sel); setOpen(false); }
    catch (err) { const d = err.response?.data?.detail; toast.error(typeof d === "string" ? d : "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{triggerNode}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle>
          <DialogDescription>Select one or more teachers to assign to this student. At least one teacher is required.</DialogDescription>
        </DialogHeader>
        {teachers.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">No teachers yet. Please create a teacher first in the Teachers tab.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto py-1" data-testid="assign-teacher-options">
            {teachers.map((t) => (
              <label key={t.id} className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-slate-50" data-testid={`assign-teacher-option-${t.id}`}>
                <input type="checkbox" checked={sel.includes(t.id)} onChange={() => toggle(t.id)} className="h-4 w-4 accent-blue-900" data-testid={`assign-teacher-checkbox-${t.id}`} />
                <span className="flex-1 min-w-0"><span className="font-semibold text-slate-800 block truncate">{t.name}</span><span className="block text-xs text-slate-500 truncate">{t.email}</span></span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button onClick={save} disabled={saving || teachers.length === 0} className="bg-blue-900 hover:bg-blue-950" data-testid="assign-teachers-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StudentManageDialog({ student, onChanged, onReload }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [sign, setSign] = useState(1);
  const [amount, setAmount] = useState(1);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [balance, setBalance] = useState(student.star_balance ?? 0);
  const [std, setStd] = useState(student.student_class || "");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/admin/students/${student.id}/transactions`); setHistory(data); }
    catch { setHistory([]); }
  }, [student.id]);

  useEffect(() => { if (open) { load(); setStd(student.student_class || ""); setBalance(student.star_balance ?? 0); } }, [open, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    const value = sign * Math.abs(parseInt(amount, 10) || 0);
    if (value === 0) { toast.error("Star change cannot be zero"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/teacher/students/${student.id}/stars`, { delta: value, reason: reason.trim() });
      setBalance(data.balance);
      setReason("");
      toast.success(`New balance: ${data.balance}`);
      load();
      onChanged && onChanged(student.id, data.balance);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed");
    } finally { setSaving(false); }
  };

  const changeStandard = async (v) => {
    setStd(v);
    try { await api.patch(`/admin/students/${student.id}`, { student_class: v }); toast.success("Standard updated"); onReload && onReload(); }
    catch { toast.error("Could not update standard"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`admin-manage-student-${student.id}`}>Manage</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{student.name} · Roll {student.roll_number}</DialogTitle>
          <DialogDescription>Adjust stars (with a reason), correct the standard, and review the immutable history.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-blue-900 text-white p-4 flex items-center justify-between">
          <span className="text-sm font-semibold text-sky-200 uppercase tracking-wide">Balance</span>
          <span className="text-2xl font-extrabold flex items-center gap-2"><Star className="h-6 w-6 fill-amber-400 text-amber-400" />{balance}</span>
        </div>
        <div>
          <Label className="mb-1.5 block font-semibold text-slate-700">Standard</Label>
          <Select value={std} onValueChange={changeStandard}>
            <SelectTrigger data-testid={`admin-student-standard-${student.id}`}><SelectValue placeholder="Select standard" /></SelectTrigger>
            <SelectContent>{STANDARDS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button type="button" onClick={() => setSign(1)} className={sign === 1 ? "bg-green-600 hover:bg-green-700 flex-1" : "flex-1"} variant={sign === 1 ? "default" : "outline"} data-testid="admin-sign-add"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            <Button type="button" onClick={() => setSign(-1)} className={sign === -1 ? "bg-red-600 hover:bg-red-700 flex-1" : "flex-1"} variant={sign === -1 ? "default" : "outline"} data-testid="admin-sign-subtract"><Minus className="h-4 w-4 mr-1" /> Subtract</Button>
          </div>
          <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="admin-stars-amount" />
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason (required)" data-testid="admin-stars-reason" />
          <Button onClick={submit} disabled={saving} className="w-full bg-blue-900 hover:bg-blue-950" data-testid="admin-stars-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
          </Button>
        </div>
        <div>
          <h4 className="flex items-center gap-2 font-bold text-slate-900 mb-2 mt-2"><History className="h-4 w-4" /> History</h4>
          {history.length === 0 ? <p className="text-sm text-slate-500">No activity yet.</p> : (
            <div className="space-y-2">
              {history.map((t) => (
                <div key={t.id} className="rounded-lg border border-slate-200 p-2.5 text-sm flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{t.reason}</p>
                    <p className="text-xs text-slate-500">{t.teacher_name} · {fmt(t.created_at)} · {t.balance_before}→{t.balance_after}</p>
                  </div>
                  <span className={`font-bold ${t.delta > 0 ? "text-green-600" : "text-red-600"}`}>{t.delta > 0 ? "+" : ""}{t.delta}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function StarsPanel() {
  const [pending, setPending] = useState([]);
  const [requests, setRequests] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newT, setNewT] = useState({ name: "", email: "", password: "", whatsapp: "", whatsapp_consent: false });
  const [tOpen, setTOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [boardEnabled, setBoardEnabled] = useState(true);
  const [stdFilter, setStdFilter] = useState("all");
  const [studentQuery, setStudentQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, t, site, rq] = await Promise.all([
        api.get("/admin/students", { params: { status: "pending" } }),
        api.get("/admin/students", { params: { status: "approved" } }),
        api.get("/admin/teachers"),
        api.get("/site"),
        api.get("/admin/star-requests"),
      ]);
      setPending(p.data); setStudents(s.data); setTeachers(t.data); setRequests(rq.data);
      setBoardEnabled(site.data?.leaderboard_enabled !== false);
    } catch { toast.error("Could not load data"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const delStudent = async (id) => { if (!window.confirm("Delete this student and all their star history?")) return; try { await api.delete(`/admin/students/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };
  const delTeacher = async (id) => { if (!window.confirm("Remove this teacher? They will be unassigned from all students.")) return; try { await api.delete(`/admin/teachers/${id}`); toast.success("Removed"); load(); } catch { toast.error("Failed"); } };

  const toggleBoard = async (enabled) => {
    setBoardEnabled(enabled);
    try { await api.post("/admin/leaderboard/toggle", { enabled }); toast.success(enabled ? "Public leaderboard is now visible" : "Public leaderboard is now hidden"); }
    catch { toast.error("Failed to update"); setBoardEnabled(!enabled); }
  };

  const toggleExclude = async (s) => {
    const excluded = !s.leaderboard_excluded;
    setStudents((list) => list.map((x) => x.id === s.id ? { ...x, leaderboard_excluded: excluded } : x));
    try { await api.post(`/admin/students/${s.id}/leaderboard`, { excluded }); toast.success(excluded ? "Hidden from public leaderboard" : "Shown on public leaderboard"); }
    catch { toast.error("Failed"); setStudents((list) => list.map((x) => x.id === s.id ? { ...x, leaderboard_excluded: !excluded } : x)); }
  };

  const createTeacher = async () => {
    if (!newT.name || !newT.email || !newT.password) { toast.error("All fields are required"); return; }
    setCreating(true);
    try {
      await api.post("/admin/teachers", newT);
      toast.success("Teacher created");
      setNewT({ name: "", email: "", password: "", whatsapp: "", whatsapp_consent: false }); setTOpen(false); load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to create teacher");
    } finally { setCreating(false); }
  };

  const onBalanceChanged = (id, bal) => setStudents((list) => list.map((x) => x.id === id ? { ...x, star_balance: bal } : x));
  const teacherNames = (ids) => (ids || []).map((id) => teachers.find((t) => t.id === id)?.name).filter(Boolean);
  const presentStandards = STANDARDS.filter((s) => students.some((x) => x.student_class === s));
  const q = studentQuery.trim().toLowerCase();
  const visibleStudents = students.filter((s) => {
    if (stdFilter !== "all" && s.student_class !== stdFilter) return false;
    if (q && !((s.name || "").toLowerCase().includes(q) || (s.roll_number || "").toLowerCase().includes(q))) return false;
    return true;
  });

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;

  return (
    <div data-testid="panel-stars">
      <h2 className="text-2xl font-bold text-slate-900 mb-1">Star System</h2>
      <p className="text-sm text-slate-500 mb-5">Approve student accounts (assigning teachers), manage star balances, teacher assignments, standards and the public leaderboard.</p>

      <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between mb-6" data-testid="leaderboard-settings">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-600"><Trophy className="h-5 w-5" /></div>
          <div>
            <p className="font-semibold text-slate-900">Public "Top 10 Star Holders"</p>
            <p className="text-xs text-slate-500">Show or hide the entire public leaderboard on the website.</p>
          </div>
        </div>
        <Switch checked={boardEnabled} onCheckedChange={toggleBoard} data-testid="leaderboard-toggle" />
      </div>

      <Tabs defaultValue="pending">
        <TabsList className="mb-6">
          <TabsTrigger value="pending" data-testid="stars-tab-pending"><Clock className="h-4 w-4 mr-1.5" /> Pending {pending.length > 0 && <Badge className="ml-2 bg-amber-500">{pending.length}</Badge>}</TabsTrigger>
          <TabsTrigger value="students" data-testid="stars-tab-students">Students ({students.length})</TabsTrigger>
          <TabsTrigger value="teachers" data-testid="stars-tab-teachers">Teachers ({teachers.length})</TabsTrigger>
          <TabsTrigger value="requests" data-testid="stars-tab-requests"><Star className="h-4 w-4 mr-1.5" /> Star Requests ({requests.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <AdmissionQrCard />
          {pending.length === 0 ? <p className="text-sm text-slate-500 py-6">No pending student accounts.</p> : (
            <div className="space-y-2" data-testid="pending-list">
              {pending.map((s) => (
                <div key={s.id} className="rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-4" data-testid={`pending-student-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900">{s.name}</p>
                    <p className="text-sm text-slate-500">{s.student_class} · Roll {s.roll_number}{s.guardian_email ? ` · ${s.guardian_email}` : ""}</p>
                    <AdmissionDetails s={s} />
                  </div>
                  <TeacherAssignDialog
                    title={`Approve ${s.name} — assign teacher(s)`}
                    actionLabel="Approve & Assign"
                    teachers={teachers}
                    initial={[]}
                    onSave={async (ids) => { await api.post(`/admin/students/${s.id}/approve`, { teacher_ids: ids }); toast.success("Student approved & teachers assigned"); load(); }}
                    triggerNode={<Button size="sm" className="bg-green-600 hover:bg-green-700" data-testid={`approve-student-${s.id}`}><Check className="h-4 w-4 mr-1" /> Approve</Button>}
                  />
                  <Button size="sm" variant="outline" onClick={() => delStudent(s.id)} className="text-red-600" data-testid={`reject-student-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="students">
          <div className="mb-4 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={studentQuery} onChange={(e) => setStudentQuery(e.target.value)} placeholder="Search by name or roll (e.g. PTG-0010)" className="pl-9" data-testid="admin-student-search" />
            </div>
            <div className="max-w-xs w-48">
              <Select value={stdFilter} onValueChange={setStdFilter}>
                <SelectTrigger data-testid="admin-standard-filter"><SelectValue placeholder="Filter by standard" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="admin-standard-all">All standards</SelectItem>
                  {presentStandards.map((s) => <SelectItem key={s} value={s} data-testid={`admin-standard-${s}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <span className="text-xs text-slate-500" data-testid="admin-student-count">{visibleStudents.length} of {students.length}</span>
          </div>
          {visibleStudents.length === 0 ? <p className="text-sm text-slate-500 py-6">{students.length === 0 ? "No approved students yet." : "No students in this standard."}</p> : (
            <div className="space-y-2" data-testid="students-list">
              {visibleStudents.map((s) => {
                const names = teacherNames(s.teacher_ids);
                return (
                  <div key={s.id} className="rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-3 flex-wrap" data-testid={`student-row-${s.id}`}>
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-900 text-white font-bold">{s.name?.charAt(0) || "?"}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate flex items-center gap-1"><span className="truncate">{s.name}</span> {s.leaderboard_excluded && <Badge variant="outline" className="text-amber-600 border-amber-300 shrink-0">Off leaderboard</Badge>}</div>
                      <p className="text-sm text-slate-500 truncate">{s.student_class} · Roll {s.roll_number}</p>
                      <p className="text-xs mt-0.5 truncate" data-testid={`student-teachers-${s.id}`}>
                        {names.length > 0
                          ? <span className="text-sky-700 font-medium">Teachers: {names.join(", ")}</span>
                          : <span className="text-amber-600 font-medium">No teacher assigned</span>}
                      </p>
                    </div>
                    <span className="flex items-center gap-1 font-bold text-amber-600"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />{s.star_balance ?? 0}</span>
                    <Button size="sm" variant="outline" onClick={() => toggleExclude(s)} className={s.leaderboard_excluded ? "text-amber-600" : "text-slate-600"} title="Show/hide on public leaderboard" data-testid={`toggle-leaderboard-${s.id}`}>
                      {s.leaderboard_excluded ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <TeacherAssignDialog
                      title={`Manage teachers — ${s.name}`}
                      actionLabel="Save Teachers"
                      teachers={teachers}
                      initial={s.teacher_ids || []}
                      onSave={async (ids) => { await api.put(`/admin/students/${s.id}/teachers`, { teacher_ids: ids }); toast.success("Teachers updated"); load(); }}
                      triggerNode={<Button size="sm" variant="outline" data-testid={`manage-teachers-${s.id}`}><Users className="h-4 w-4 mr-1" /> Teachers</Button>}
                    />
                    <StudentManageDialog student={s} onChanged={onBalanceChanged} onReload={load} />
                    <Button size="sm" variant="outline" onClick={() => delStudent(s.id)} className="text-red-600" data-testid={`delete-student-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="teachers">
          <Dialog open={tOpen} onOpenChange={setTOpen}>
            <DialogTrigger asChild><Button className="mb-4 bg-blue-900 hover:bg-blue-950" data-testid="add-teacher-btn"><UserPlus className="h-4 w-4 mr-1.5" /> Add Teacher</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Teacher</DialogTitle>
                <DialogDescription>Create a teacher login. Teachers sign in with their email and password.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label className="mb-1.5 block font-semibold text-slate-700">Name</Label><Input value={newT.name} onChange={(e) => setNewT((f) => ({ ...f, name: e.target.value }))} data-testid="teacher-name-input" /></div>
                <div><Label className="mb-1.5 block font-semibold text-slate-700">Email</Label><Input type="email" value={newT.email} onChange={(e) => setNewT((f) => ({ ...f, email: e.target.value }))} data-testid="teacher-email-input" /></div>
                <div><Label className="mb-1.5 block font-semibold text-slate-700">Password</Label><Input value={newT.password} onChange={(e) => setNewT((f) => ({ ...f, password: e.target.value }))} data-testid="teacher-password-input" /></div>
                <div><Label className="mb-1.5 block font-semibold text-slate-700">WhatsApp number (India)</Label><Input value={newT.whatsapp} onChange={(e) => setNewT((f) => ({ ...f, whatsapp: e.target.value }))} placeholder="9833388201" data-testid="teacher-whatsapp-input" /></div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={newT.whatsapp_consent} onChange={(e) => setNewT((f) => ({ ...f, whatsapp_consent: e.target.checked }))} className="h-4 w-4 accent-blue-900" data-testid="teacher-consent-input" />
                  Teacher has consented to receive WhatsApp reminders
                </label>
              </div>
              <DialogFooter>
                <Button onClick={createTeacher} disabled={creating} className="bg-blue-900 hover:bg-blue-950" data-testid="create-teacher-submit">{creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {teachers.length === 0 ? <p className="text-sm text-slate-500 py-6">No teachers yet.</p> : (
            <div className="space-y-2" data-testid="teachers-list">
              {teachers.map((t) => (
                <div key={t.id} className="rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-4" data-testid={`teacher-row-${t.id}`}>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-600 text-white font-bold">{t.name?.charAt(0) || "T"}</div>
                  <div className="flex-1 min-w-0"><p className="font-semibold text-slate-900 truncate">{t.name}</p><p className="text-sm text-slate-500 truncate">{t.email}{t.whatsapp ? ` · ${t.whatsapp}` : ""}{t.whatsapp && t.whatsapp_consent ? " ✓consent" : t.whatsapp ? " (no consent)" : ""}</p></div>
                  <TeacherWhatsAppDialog teacher={t} onDone={load} />
                  <TeacherResetPasswordDialog teacher={t} onDone={load} />
                  <Button size="sm" variant="outline" onClick={() => delTeacher(t.id)} className="text-red-600" data-testid={`delete-teacher-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests">
          {requests.length === 0 ? <p className="text-sm text-slate-500 py-6">No star requests yet.</p> : (
            <div className="space-y-2" data-testid="admin-requests-list">
              {requests.map((r) => (
                <div key={r.id} className="rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-4" data-testid={`admin-request-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{r.student_name} · {r.student_class} · Roll {r.roll_number}</p>
                    <p className="text-sm text-slate-600 truncate">"{r.reason}"</p>
                    <p className="text-xs text-slate-500 mt-0.5">Requested {r.requested_stars}★{r.approved_stars != null ? ` · Approved ${r.approved_stars}★` : ""}{r.decided_by ? ` · by ${r.decided_by}` : ""} · {fmt(r.created_at)}</p>
                  </div>
                  <Badge className={r.status === "approved" ? "bg-green-600" : r.status === "rejected" ? "bg-red-600" : "bg-amber-500"} data-testid={`admin-request-status-${r.id}`}>{r.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
